import { useEffect } from "react";

import { useLastKnownTotal, useTableState } from "@/ds";
import {
  DEFAULT_FINANCEIRO_OBRAS_SORT,
  isFinanceiroObrasSortField,
} from "@/modules/financeiro-obras";
import type { FinanceiroObrasFilters, FinanceiroObrasSort } from "@/modules/financeiro-obras";
import { useFinanceiroObrasLista } from "./use-listing-query";

interface UseFinanceiroObrasListaPaginaParams {
  readonly search: string;
  readonly filters: FinanceiroObrasFilters;
  readonly sort: FinanceiroObrasSort | null;
  /** Informa o total da consulta à tela (Exportar Visualização não baixa lista vazia). */
  readonly onResult: (result: { status: string; total: number }) => void;
}

/** Model da Lista: a consulta paginada no servidor (20 por página); a página vem do `useTableState` do DS. */
export function useFinanceiroObrasListaPagina({
  search,
  filters,
  sort,
  onResult,
}: UseFinanceiroObrasListaPaginaParams) {
  const { lastTotal, remember } = useLastKnownTotal();
  const pagina = useTableState({
    isSortField: isFinanceiroObrasSortField,
    total: lastTotal,
    resetKey: JSON.stringify([search.trim(), filters, sort]),
  });

  const { items, total, status, errorMessage, reload } = useFinanceiroObrasLista({
    search,
    filters,
    page: pagina.page + 1,
    pageSize: pagina.rows,
    sort: sort ?? DEFAULT_FINANCEIRO_OBRAS_SORT,
  });
  remember(status, total);

  useEffect(() => {
    onResult({ status, total });
  }, [onResult, status, total]);

  return {
    items,
    total,
    status,
    errorMessage,
    reload,
    first: pagina.first,
    rows: pagina.rows,
    onPage: pagina.onPage,
  };
}
